import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import axios from "axios"; // Import axios for API calls
import { jwtDecode } from "jwt-decode";

export interface UserData {
  user_id: number;
  id?: number;
  email: string;
  first_name: string;
  last_name: string;
  address: string;
  city: string;
  state_code: string;
  zip: string;
  role: string;
  user_access?: {
    tags?: string[];
    user_status?: {
      Admin?: string;
      Active?: string;
      Verified?: string;
    };
  };
  user_settings?: any;
  stripe_customer_id?: string;
  created_at: string;
  updated_at: string;
  avatar_url?: any;
  accounts_json?: any;
  brands_json?: any;
  agencies_json?: any;
  BAs_json?: any;
  role_data_json?: any;
}

interface UserContextType {
  currentUser: UserData | null;
  isLoggedIn: boolean;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserData | null>>;
  selectedAccountId: number | null;
  setselectedAccountId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedBrandId: number | null;
  setSelectedBrandId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedAmbassadorId: number | null;
  setSelectedAmbassadorId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedAgencyID: number | null;
  setSelectedAgencyID: React.Dispatch<React.SetStateAction<number | null>>;
  selectedAvatar: string | null;
  setSelectedAvatar: React.Dispatch<React.SetStateAction<string | null>>;
  handleUserSelect: (userId: number) => Promise<void>;
  handleAccountSelect: (id: number) => void;
  handleBrandSelect: (id: number) => void;
  handleAmbassadorSelect: (id: number) => void;
  handleAgencySelect: (id: number) => void;
  updateUserData: (updatedData: UserData) => void;
  selectedRoleId: number | null;
  setSelectedRoleId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedSupplierId: number | null;
  setSelectedSupplierId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedOwnerId: number | null;
  setSelectedOwnerId: React.Dispatch<React.SetStateAction<number | null>>;
  login: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<void>;
  logout: () => void;
}

export interface UserProvidedProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<UserProvidedProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [selectedAccountId, setselectedAccountId] = useState<number | null>(
    null
  );
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [selectedAmbassadorId, setSelectedAmbassadorId] = useState<
    number | null
  >(null);
  const [selectedAgencyID, setSelectedAgencyID] = useState<number | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    null
  );
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null);

  // Effect to log selected IDs whenever they change
  useEffect(() => {
    if (selectedAmbassadorId !== null) {
      handleAmbassadorSelect(selectedAmbassadorId);
    }
    if (selectedAccountId !== null) {
      handleAccountSelect(selectedAccountId);
    }
    if (selectedBrandId !== null) {
      handleBrandSelect(selectedBrandId);
    }
    if (selectedAgencyID !== null) {
      handleAgencySelect(selectedAgencyID);
    }
  }, [
    selectedAmbassadorId,
    selectedAccountId,
    selectedBrandId,
    selectedAgencyID,
  ]);

  // Add function to check token and update user data
  const checkToken = () => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token) as UserData;
        setCurrentUser(decoded);
        setIsLoggedIn(true);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } catch (error) {
        console.error("Token validation error:", error);
        // Clear invalid token
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        setCurrentUser(null);
        setIsLoggedIn(false);
        delete axios.defaults.headers.common["Authorization"];
      }
    }
  };

  // Check token on component mount
  useEffect(() => {
    checkToken();
  }, []);

  // New function to handle user selection
  const handleUserSelect = async (userId: number) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/users/${userId}/data`
      );
      const userData = response.data;

      const updatedUser = {
        user_id: userData.user_id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        address: userData.address,
        city: userData.city,
        state_code: userData.state_code,
        zip: userData.zip,
        role: userData.role,
        avatar_url: userData.avatar_url,
        user_access: userData.user_access,
        user_settings: userData.user_settings,
        stripe_customer_id: userData.stripe_customer_id,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
        ...(userData.role === "Account" || userData.role === "Owner"
          ? {
              accounts_json: Array.isArray(userData.role_data_json)
                ? userData.role_data_json
                : [],
            }
          : {}),
        ...(userData.role === "Brand" || userData.role === "Supplier"
          ? {
              brands_json: Array.isArray(userData.role_data_json)
                ? userData.role_data_json
                : [],
            }
          : {}),
        ...(userData.role === "Agency"
          ? {
              agencies_json: Array.isArray(userData.role_data_json)
                ? userData.role_data_json
                : [],
            }
          : {}),
        ...(userData.role === "BA"
          ? {
              BAs_json: Array.isArray(userData.role_data_json)
                ? userData.role_data_json
                : [],
            }
          : {}),
      };

      // Set the current user first
      setCurrentUser(updatedUser);
      console.log("updatedUser", updatedUser);
      // Reset other IDs when a user is selected
      setselectedAccountId(null);
      setSelectedBrandId(null);
      setSelectedAgencyID(null);
      setSelectedRoleId(null);
      setSelectedAmbassadorId(userData.role === "BA" ? userData.user_id : null);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // Updated handleAccountSelect function
  const handleAccountSelect = (id: number | null) => {
    setSelectedBrandId(null); // Reset brand ID
    setSelectedAmbassadorId(null); // Reset ambassador ID
    setSelectedAgencyID(null); // Reset agency ID
    setselectedAccountId(id);
    setSelectedRoleId(id);

    // Find the account in accounts_json and set its owner_id
    if (id !== null && currentUser?.accounts_json) {
      const selectedAccount = currentUser.accounts_json.find(
        (account: { account_id: number; owner_id: number }) =>
          account.account_id === id
      );
      setSelectedOwnerId(selectedAccount?.owner_id || null);
    } else {
      setSelectedOwnerId(null);
    }

    if (id !== null && currentUser?.avatar_url) {
      const selectedAvatarObj = currentUser.avatar_url.find(
        (avatar: { id: number; avatar_url: string }) => avatar.id === id
      );
      const avatarUrl = selectedAvatarObj ? selectedAvatarObj.avatar_url : null;
      setSelectedAvatar(avatarUrl);
    }
  };

  // New function to handle brand selection
  const handleBrandSelect = (id: number | null) => {
    setselectedAccountId(null); // Reset account ID
    setSelectedAmbassadorId(null); // Reset ambassador ID
    setSelectedAgencyID(null); // Reset agency ID
    setSelectedBrandId(id);
    setSelectedRoleId(id);

    // Find the brand in brands_json and set its supplier_id
    if (id !== null && currentUser?.brands_json) {
      const selectedBrand = currentUser.brands_json.find(
        (brand: { brand_id: number; supplier_id: number }) =>
          brand.brand_id === id
      );
      setSelectedSupplierId(selectedBrand?.supplier_id || null);
    } else {
      setSelectedSupplierId(null);
    }

    // Update selectedAvatar based on the selected brand
    if (id !== null && currentUser?.avatar_url) {
      const selectedAvatarObj = currentUser.avatar_url.find(
        (avatar: { id: number; avatar_url: string }) => avatar.id === id
      );
      const avatarUrl = selectedAvatarObj ? selectedAvatarObj.avatar_url : null;
      setSelectedAvatar(avatarUrl);
    }
    console.log("selectedSupplierId", selectedSupplierId);
  };

  // New function to handle ambassador selection
  const handleAmbassadorSelect = (id: number | null) => {
    setselectedAccountId(null); // Reset account ID
    setSelectedBrandId(null); // Reset brand ID
    setSelectedAgencyID(null); // Reset agency ID
    setSelectedAmbassadorId(id);
    setSelectedRoleId(id);
    // Update selectedAvatar based on the selected ambassador
    if (id !== null && currentUser?.avatar_url) {
      const selectedAvatarObj = currentUser.avatar_url.find(
        (avatar: { id: number; avatar_url: string }) => avatar.id === id
      );
      const avatarUrl = selectedAvatarObj ? selectedAvatarObj.avatar_url : null; // Get the avatar_url
      setSelectedAvatar(avatarUrl);
    }
  };

  // New function to handle agency selection
  const handleAgencySelect = (id: number | null) => {
    setselectedAccountId(null); // Reset account ID
    setSelectedBrandId(null); // Reset brand ID
    setSelectedAmbassadorId(null); // Reset ambassador ID
    setSelectedAgencyID(id);
    setSelectedRoleId(id);
    // Update selectedAvatar based on the selected agency
    if (id !== null && currentUser?.avatar_url) {
      const selectedAvatarObj = currentUser.avatar_url.find(
        (avatar: { id: number; avatar_url: string }) => avatar.id === id
      );
      const avatarUrl = selectedAvatarObj ? selectedAvatarObj.avatar_url : null; // Get the avatar_url
      setSelectedAvatar(avatarUrl);
    }
  };

  const updateUserData = (updatedData: UserData) => {
    setCurrentUser(updatedData);
    // Optionally, you can also update roleJsonData here if needed
  };

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean
  ) => {
    try {
      console.log(
        "Making login request to:",
        `${import.meta.env.VITE_API_URL}/users/login`
      );
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/users/login`,
        {
          username: email,
          password: password,
        }
      );

      const { token, user } = response.data;
      console.log("Login response received:", {
        hasToken: !!token,
        userData: {
          ...user,
          role: user.role,
          role_data_json: user.role_data_json ? "present" : "missing",
          user_id: user.user_id,
          id: user.id, // Check if it's using 'id' instead of 'user_id'
          rawUser: user, // Log the raw user object
        },
      });

      // Store the token in localStorage if rememberMe is true
      if (rememberMe) {
        localStorage.setItem("token", token);
      } else {
        sessionStorage.setItem("token", token);
      }

      // Set the current user from the user_users table data
      setCurrentUser(user);
      setIsLoggedIn(true);

      // Set default axios authorization header
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } catch (error) {
      console.error("Login error details:", error);
      throw error;
    }
  };

  const logout = () => {
    // Clear token from storage
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");

    // Clear user data and auth header
    setCurrentUser(null);
    setIsLoggedIn(false);
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <UserContext.Provider
      value={{
        currentUser,
        isLoggedIn,
        setCurrentUser,
        selectedAccountId,
        setselectedAccountId,
        selectedBrandId,
        setSelectedBrandId,
        selectedAmbassadorId,
        setSelectedAmbassadorId,
        selectedAgencyID,
        setSelectedAgencyID,
        selectedAvatar,
        setSelectedAvatar,
        handleUserSelect,
        handleAccountSelect,
        handleBrandSelect,
        handleAmbassadorSelect,
        handleAgencySelect,
        updateUserData,
        selectedRoleId,
        setSelectedRoleId,
        selectedSupplierId,
        setSelectedSupplierId,
        selectedOwnerId,
        setSelectedOwnerId,
        login,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
